import { useCallback, useEffect } from 'react';
import EditHubStudentReservationSettings from 'components/AppService/HubSettings/Student/ReservationSettings/EditHubStudentReservationSettings';
import StudentReservationStatus from 'components/AppService/HubSettings/Student/ReservationSettings/StudentReservationStatus';
import LoadingWrapper from 'components/LoadingWrapper';
import { fetchFields, fieldsLoading } from 'slices/field.slice';
import {
  fetchHubStudentSettings,
  hubStudentSettingsLoadingSelector,
  saveHubStudentSettings,
} from 'slices/hubStudentSettings.slice';
import {
  fetchObjectMapping,
  requestObjectTypes,
  selectObjectTypesLoading,
} from 'slices/objectTypes.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { fetchTimeZones } from 'slices/timeZone.slice';
import {
  Button,
  DefaultContentLayout,
  Typography,
} from '@timeedit/ui-components';
import { DateLimits } from '../../../components/DateLimits/DateLimits';
import intl from '../../../i18n/intl';
import {
  hubStudentReservationSettingOnChange,
  hubStudentReservationSettingOnClear,
  hubStudentSettingsSelector,
} from '../../../slices/hubStudentSettings.slice';
import { DateLimit } from '../../../types/hubStudentSettings.type';
import { ReservationMode } from '../allocateSettings/components/ReservationMode';

const language = intl.messages as Record<string, string>;

const RESERVATION_MODE_PATH = 'reservationMode';
const DATE_LIMIT_PATH = 'datelimit';

const HubStudentReservationSettingsPage = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(fetchTimeZones());
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
    dispatch(fetchObjectMapping());
    dispatch(fetchHubStudentSettings());
  }, []);

  const allFieldsLoading = useAppSelector(fieldsLoading);
  const allObjectTypesLoading = useAppSelector(selectObjectTypesLoading);
  const configLoading = useAppSelector(hubStudentSettingsLoadingSelector);

  const onSave = useCallback(() => dispatch(saveHubStudentSettings()), []);

  const isLoading = allFieldsLoading || allObjectTypesLoading || configLoading;

  const config = useAppSelector(hubStudentSettingsSelector);

  const mode = config?.studentReservationDisplaySettings?.reservationMode;
  const datelimit = config?.studentReservationDisplaySettings?.datelimit;

  const setMode = useCallback(
    (value: number) => {
      if (value === 0) {
        dispatch(
          hubStudentReservationSettingOnClear({ path: RESERVATION_MODE_PATH })
        );
      } else {
        dispatch(
          hubStudentReservationSettingOnChange({
            path: RESERVATION_MODE_PATH,
            value,
          })
        );
      }
    },
    [dispatch]
  );

  const setDateLimit = useCallback(
    (value: DateLimit) => {
      dispatch(
        hubStudentReservationSettingOnChange({
          path: DATE_LIMIT_PATH,
          value,
        })
      );
    },
    [dispatch]
  );

  return (
    <DefaultContentLayout
      title="General reservation settings"
      subtitle="Configure what reservations are shown and what reservation information is presented to students"
      actions={
        <Button type="primary" onClick={onSave}>
          {language.save_changes}
        </Button>
      }
    >
      <LoadingWrapper isLoading={isLoading}>
        {!isLoading && (
          <div style={{ margin: '10px' }}>
            <Typography.Title level={4}>Reservations to show</Typography.Title>
            <StudentReservationStatus />
            <ReservationMode mode={mode} setMode={setMode} />
            <DateLimits datelimit={datelimit} setDatelimit={setDateLimit} />
            <EditHubStudentReservationSettings />
          </div>
        )}
      </LoadingWrapper>
    </DefaultContentLayout>
  );
};

export default HubStudentReservationSettingsPage;
